import { useState, useMemo, useCallback, useRef } from 'react';
import {
  Search, ChevronUp, ChevronDown, ChevronsUpDown,
  Check, X, Download, Trash2, Mail, MoreHorizontal,
  ChevronLeft, ChevronRight, Loader2, Filter
} from 'lucide-react';

/**
 * AdminListView — Reusable list component for back-office
 *
 * Props:
 * @param {Array} data          - Array of objects to display
 * @param {Array} columns       - Column definitions: { key, label, render?, sortable?, width? }
 * @param {Function} onRowClick - Called when a row is clicked (optional)
 * @param {Array} filters       - Filter definitions: { key, label, options: [{value, label}], value, onChange }
 * @param {string} searchValue  - Controlled search value
 * @param {Function} onSearchChange - Search change handler
 * @param {string} searchPlaceholder - Search placeholder text
 * @param {Array} bulkActions   - Bulk action definitions: { label, icon, onClick, variant? }
 * @param {boolean} loading     - Loading state
 * @param {string} emptyMessage - Message when no data
 * @param {string} idKey        - Key to use as unique identifier (default: 'id')
 * @param {number} pageSize     - Items per page (default: 25)
 * @param {number} totalCount   - Total count for display (optional, defaults to data.length)
 * @param {React.ReactNode} headerActions - Additional header actions (buttons, etc.)
 */
const AdminListView = ({
  data = [],
  columns = [],
  onRowClick,
  filters = [],
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Rechercher...',
  bulkActions = [],
  loading = false,
  emptyMessage = 'Aucun élément',
  idKey = 'id',
  pageSize = 25,
  totalCount,
  headerActions,
}) => {
  const [selected, setSelected] = useState(new Set());
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(0);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    const col = columns.find(c => c.key === sortKey);
    if (!col) return data;
    return [...data].sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (va == null) va = '';
      if (vb == null) vb = '';
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      const sa = String(va).toLowerCase(), sb = String(vb).toLowerCase();
      return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [data, sortKey, sortDir, columns]);

  // Paginate
  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const pagedData = sortedData.slice(page * pageSize, (page + 1) * pageSize);

  const handleSort = useCallback((key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }, [sortKey]);

  const toggleSelectAll = useCallback(() => {
    if (selected.size === pagedData.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pagedData.map(r => r[idKey])));
    }
  }, [pagedData, selected, idKey]);

  const toggleSelect = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = () => setSelected(new Set());
  const allSelected = pagedData.length > 0 && pagedData.every(r => selected.has(r[idKey]));
  const someSelected = selected.size > 0 && !allSelected && pagedData.some(r => selected.has(r[idKey]));
  const displayCount = totalCount != null ? totalCount : data.length;

  // Clear selection when filters/search/data change
  const dataSignature = `${data.length}-${searchValue}`;
  const prevSignature = useRef(dataSignature);
  if (prevSignature.current !== dataSignature) {
    prevSignature.current = dataSignature;
    if (selected.size > 0) setSelected(new Set());
  }

  return (
    <div className="flex flex-col h-full" data-testid="admin-list-view">
      {/* ─── TOOLBAR ──────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-100 px-4 py-3 flex flex-col gap-2">
        {/* Row 1: Search + count + actions */}
        <div className="flex items-center gap-3">
          {onSearchChange && (
            <div className="relative flex-1 max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={searchValue}
                onChange={e => { onSearchChange(e.target.value); setPage(0); }}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#35AFE4]/30 focus:border-[#35AFE4]"
                data-testid="list-search-input"
              />
            </div>
          )}
          <span className="text-xs text-gray-400 font-medium whitespace-nowrap" data-testid="list-count">
            {displayCount} élément{displayCount !== 1 ? 's' : ''}
          </span>
          <div className="flex-1" />
          {headerActions}
        </div>

        {/* Row 2: Filters */}
        {filters.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="list-filters">
            <Filter size={13} className="text-gray-400" />
            {filters.map((f, i) => (
              <select
                key={i}
                value={f.value}
                onChange={e => { f.onChange(e.target.value); setPage(0); }}
                className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#35AFE4] text-gray-700"
                data-testid={`filter-${f.key}`}
              >
                <option value="">{f.label}</option>
                {f.options.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ))}
          </div>
        )}

        {/* Bulk actions bar */}
        {selected.size > 0 && bulkActions.length > 0 && (
          <div className="flex items-center gap-2 bg-[#35AFE4]/5 border border-[#35AFE4]/20 rounded-lg px-3 py-2" data-testid="bulk-actions-bar">
            <span className="text-xs font-bold text-[#35AFE4]">{selected.size} sélectionné{selected.size > 1 ? 's' : ''}</span>
            <div className="flex-1" />
            {bulkActions.map((action, i) => {
              const Icon = action.icon;
              return (
                <button
                  key={i}
                  onClick={() => action.onClick([...selected])}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                    action.variant === 'danger'
                      ? 'bg-red-50 text-red-600 hover:bg-red-100'
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`bulk-action-${i}`}
                >
                  {Icon && <Icon size={13} />}
                  {action.label}
                </button>
              );
            })}
            <button onClick={clearSelection} className="p-1 rounded hover:bg-gray-100" data-testid="clear-selection">
              <X size={14} className="text-gray-400" />
            </button>
          </div>
        )}
      </div>

      {/* ─── TABLE ────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : pagedData.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-sm">{emptyMessage}</p>
          </div>
        ) : (
          <table className="w-full" data-testid="list-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                {bulkActions.length > 0 && (
                  <th className="w-10 px-3 py-2.5">
                    <button onClick={toggleSelectAll} className="w-4 h-4 rounded border border-gray-300 flex items-center justify-center hover:border-[#35AFE4] transition-colors" data-testid="select-all-checkbox">
                      {allSelected && <Check size={10} className="text-[#35AFE4]" />}
                      {someSelected && !allSelected && <span className="w-2 h-0.5 bg-[#35AFE4] rounded" />}
                    </button>
                  </th>
                )}
                {columns.map(col => (
                  <th key={col.key} className="text-left px-3 py-2.5" style={col.width ? { width: col.width } : {}}>
                    {col.sortable !== false ? (
                      <button
                        onClick={() => handleSort(col.key)}
                        className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-gray-400 hover:text-gray-700 transition-colors"
                        data-testid={`sort-${col.key}`}
                      >
                        {col.label}
                        {sortKey === col.key ? (
                          sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                        ) : (
                          <ChevronsUpDown size={12} className="opacity-30" />
                        )}
                      </button>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">{col.label}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedData.map(row => {
                const rowId = row[idKey];
                const isSelected = selected.has(rowId);
                return (
                  <tr
                    key={rowId}
                    className={`border-b border-gray-50 transition-colors ${
                      isSelected ? 'bg-[#35AFE4]/5' : 'hover:bg-gray-50'
                    } ${onRowClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onRowClick?.(row)}
                    data-testid={`list-row-${rowId}`}
                  >
                    {bulkActions.length > 0 && (
                      <td className="w-10 px-3 py-2.5" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => toggleSelect(rowId)}
                          className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                            isSelected ? 'border-[#35AFE4] bg-[#35AFE4]' : 'border-gray-300 hover:border-[#35AFE4]'
                          }`}
                          data-testid={`select-${rowId}`}
                        >
                          {isSelected && <Check size={10} className="text-white" />}
                        </button>
                      </td>
                    )}
                    {columns.map(col => (
                      <td key={col.key} className="px-3 py-2.5 text-sm text-gray-700">
                        {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '--')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ─── PAGINATION ───────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="bg-white border-t border-gray-100 px-4 py-2.5 flex items-center justify-between" data-testid="list-pagination">
          <span className="text-[11px] text-gray-400">
            {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sortedData.length)} sur {sortedData.length}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="page-prev">
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageNum = totalPages <= 5 ? i : Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
              return (
                <button key={pageNum} onClick={() => setPage(pageNum)}
                  className={`w-7 h-7 rounded-lg text-[11px] font-bold transition-colors ${
                    page === pageNum ? 'bg-[#1e3a5f] text-white' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                  data-testid={`page-${pageNum}`}>
                  {pageNum + 1}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="page-next">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminListView;
